/**
 * Maps dev backend list responses to the shape the admin UI was built for.
 *
 * Dev (navo_api): { total, data: T[], page, limit, totalPages }
 * Legacy UI expects: { data: { [itemsKey]: T[], pagination: { totalItems, totalPages, ... } } }
 */

export interface StandardPagination {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  hasMore: boolean;
}

function mapPagination(pag: Record<string, unknown>, fallbackLimit = 10): StandardPagination {
  const totalItems = Number(pag.totalItems ?? pag.total ?? 0);
  const limit = Number(pag.limit ?? fallbackLimit) || fallbackLimit;
  const currentPage = Number(pag.currentPage ?? pag.page ?? 1);
  const totalPages = Number(
    pag.totalPages ?? Math.max(1, Math.ceil(totalItems / limit) || 1),
  );
  return {
    totalItems,
    totalPages,
    currentPage,
    limit,
    hasMore: pag.hasMore === true ? true : currentPage < totalPages,
  };
}

export function normalizePaginatedList<T>(
  body: unknown,
  itemsKey: string,
): { items: T[]; pagination: StandardPagination } {
  const raw = (body ?? {}) as Record<string, unknown>;

  // Top-level array (e.g. vendors)
  if (Array.isArray(raw)) {
    const items = raw as T[];
    const limit = items.length || 10;
    return {
      items,
      pagination: {
        totalItems: items.length,
        totalPages: 1,
        currentPage: 1,
        limit,
        hasMore: false,
      },
    };
  }

  const nested = raw.data;

  // { data: { [itemsKey]: [], pagination } } or transactions-style nested
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>;
    if (inner.pagination && typeof inner.pagination === "object") {
      const items =
        (inner[itemsKey] as T[]) ??
        (Array.isArray(inner.items) ? (inner.items as T[]) : []);
      return {
        items,
        pagination: mapPagination(inner.pagination as Record<string, unknown>),
      };
    }
    if (Array.isArray(inner[itemsKey])) {
      const items = inner[itemsKey] as T[];
      return {
        items,
        pagination: {
          totalItems: items.length,
          totalPages: 1,
          currentPage: 1,
          limit: items.length || 10,
          hasMore: false,
        },
      };
    }
    // tracking: { data: { filtered: [] } }
    if (Array.isArray(inner.filtered)) {
      const items = inner.filtered as T[];
      return {
        items,
        pagination: {
          totalItems: items.length,
          totalPages: 1,
          currentPage: 1,
          limit: items.length || 10,
          hasMore: false,
        },
      };
    }
  }

  // { data: T[], pagination } (support tickets) or flat dev paginated
  if (Array.isArray(raw.data)) {
    const items = raw.data as T[];
    if (raw.pagination && typeof raw.pagination === "object") {
      return {
        items,
        pagination: mapPagination(raw.pagination as Record<string, unknown>),
      };
    }
    const limit = Number(raw.limit ?? 10) || 10;
    const totalItems = Number(raw.total ?? items.length);
    const totalPages = Number(
      raw.totalPages ?? Math.max(1, Math.ceil(totalItems / limit)),
    );
    const currentPage = Number(raw.page ?? 1);
    return {
      items,
      pagination: {
        totalItems,
        totalPages,
        currentPage,
        limit,
        hasMore: currentPage < totalPages,
      },
    };
  }

  // Support tickets: { data: items[], pagination } at root
  if (Array.isArray(raw.data) === false && raw.pagination && Array.isArray(nested)) {
    const items = nested as T[];
    return {
      items,
      pagination: mapPagination(raw.pagination as Record<string, unknown>),
    };
  }

  if (raw.pagination && typeof raw.pagination === "object" && Array.isArray(nested)) {
    return {
      items: nested as T[],
      pagination: mapPagination(raw.pagination as Record<string, unknown>),
    };
  }

  return {
    items: [],
    pagination: {
      totalItems: 0,
      totalPages: 1,
      currentPage: 1,
      limit: 10,
      hasMore: false,
    },
  };
}

export type LegacyListData<K extends string, T> = {
  pagination: StandardPagination;
} & Record<K, T[]>;

/** Wrap as legacy `{ status, data: { [itemsKey], pagination } }` for list pages. */
export function toLegacyListResponse<T, K extends string>(
  body: unknown,
  itemsKey: K,
): { status: string; data: LegacyListData<K, T> } {
  const { items, pagination } = normalizePaginatedList<T>(body, itemsKey);
  return {
    status: "success",
    data: {
      [itemsKey]: items,
      pagination,
    } as LegacyListData<K, T>,
  };
}
